import {
  AppBar,
  Button,
  Container,
  createMuiTheme,
  Dialog,
  IconButton,
  ThemeProvider,
  Toolbar,
  Typography,
} from '@material-ui/core';
import {green} from '@material-ui/core/colors';
import CloseIcon from '@material-ui/icons/Close';
import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import AgentData from 'ApplicationEntry/components/WorkFlow/BillingAgent/AgentData';
import 'ApplicationEntry/components/WorkFlow/ContingentBeneficiary/ContingentBeneficiary.sass';
import * as AR from 'ApplicationEntry/ducks/agentInformation';
import {enableSaveButtonAgent} from 'ApplicationEntry/models/AgentInformation';
import {InvalidApplication, ValidApplication} from 'ApplicationEntry/models/Application';
import React, { useEffect } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Loading from 'Shared/components/Loading';
import * as BAActions from 'ApplicationEntry/ducks/billingAgent';

const theme = createMuiTheme({
  palette: {
    primary: green,
  },
});

type DialogProps = {
  dialogOpen: boolean;
  dialogClose: () => void;
  dialogTitle: string;
  dialogSubTitle: string;
  dialogAdditional: string;
  agentIndex: number;
  editAgent: boolean;
  application: InvalidApplication | ValidApplication;
};

const AgentDialog = ({
  dialogOpen,
  dialogClose,
  dialogTitle,
  dialogSubTitle,
  agentIndex,
  editAgent,
  application,
}: DialogProps) => {
  const applicationState = useSelector((state: ReducerState) => state.application);

  const dispatchRedux = useDispatch();

  if (applicationState.key === 'LoadingModalApplication') {
    return <Loading />;
  } else if (applicationState.key === 'UndeterminedModalApplication') {
    return <Redirect to={ApplicationEntryRouteConstants.WorkQueue} />;
  }

  if (editAgent && application.agentList.agentList.toArray()[agentIndex].key === 'UnvalidatedAgent') {
    dispatchRedux(AR.editAgent(application.agentList.agentList.toArray()[agentIndex]));
  }

  const dispatch = useDispatch();
  const handleSave = () => {
    dispatchRedux(AR.isAllstateAffiliated(application.agentInformation, editAgent, agentIndex));
    if (application.billingAgent.pac.valueOf().toString() === 'false') {
      dispatch(BAActions.getAchPayerName(''));
    }
    dialogClose();
  };

  const checkTotalPercent = () => {
    const totalPercentage =
      application.agentList.agentList.reduce((a, b) => a + b.percent, 0) + application.agentInformation.percent;
    return editAgent
      ? totalPercentage - application.agentList.agentList.toArray()[agentIndex].percent
      : totalPercentage;
  };

  const checkDuplicateAgent = () => {
    return application.agentList?.agentList?.some(agent => {
      if (editAgent == false) {
        if (
          agent.agentNumber?.key === 'UserAssignedAgentNumber' &&
          application.agentInformation?.agentNumber?.key === 'UserAssignedAgentNumber'
        ) {
          if (agent.agentNumber?.value === application.agentInformation?.agentNumber?.value) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
  };

  const checkSigned = () => {
    const signed = application.agentList.agentList.some(item => item.signed === true);

    return signed;
  };

  const handleCancel = () => {
    dispatchRedux(AR.emptyAgent());
    dialogClose();
  };

  return (
    <Dialog fullScreen open={dialogOpen} onClose={dialogClose}>
      <AppBar className="contingent-beneficiary-section__dialog-header">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={dialogClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className="contingent-beneficiary-section__dialog-title">
            {dialogTitle}
          </Typography>
        </Toolbar>
      </AppBar>

      <div className="contingent-beneficiary-section__dialog ">
        <Container className="contingent-beneficiary-section__dialog-container" maxWidth={false}>
          <div className="contingent-beneficiary-section__dialog-container__title-container">
            <Typography
              variant="subtitle1"
              className="contingent-beneficiary-section__dialog-container__title-container-title"
              color="primary"
            >
              {dialogSubTitle}
            </Typography>
          </div>

          <AgentData
            agentIndex={agentIndex}
            editAgent={editAgent}
            application={application}
            agentListLength={application.agentList.agentList.size}
            totalPercent={checkTotalPercent()}
            signedValue={checkSigned()}
            checkDuplicateAgent={checkDuplicateAgent()}
          />

          <div className="primary-beneficiary-section__buttons-container">
            <Button
              variant="contained"
              color="inherit"
              onClick={handleCancel}
              className="primary-beneficiary-section__buttons"
            >
              Cancel
            </Button>
            <ThemeProvider theme={theme}>
              <Button
                variant="contained"
                color="primary"
                className="primary-beneficiary-section__buttons"
                onClick={handleSave}
                disabled={
                  !enableSaveButtonAgent(application.agentInformation) ||
                  checkTotalPercent() > 100 ||
                  checkDuplicateAgent()
                }
              >
                Save Changes
              </Button>
            </ThemeProvider>
          </div>
        </Container>
      </div>
    </Dialog>
  );
};

export default AgentDialog;
